import type { Capability, ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useState } from "react";

import { Button } from "@/common/ui/button";

import type { OfferingCatalogEntry, ScopeStateView } from "../entitlements-api";
import { offeringWriteBlock, useOfferingMutation } from "./offering-mutation";
import { sectionKey, sectionLabel } from "./offering-sections";
import { buildOfferingSections, type OfferingSection } from "./offering-state";
import { OfferingGrid } from "./OfferingGrid";

export interface OfferingsTabProps {
  /** Null until the catalog has answered; an empty array is a real answer. */
  readonly offerings: readonly OfferingCatalogEntry[] | null;
  /**
   * Where a write lands, owned by the page.
   *
   * Passed alongside `scopeState` rather than read out of it because the view
   * goes null for a render on every refresh, and this tab has to survive that
   * (see the note on the component). The two cannot disagree: `useScopeState`
   * never returns a view for any scope but the one on screen, and the guard
   * below refuses to render cards if that ever stops being true.
   */
  readonly scope: ScopeRef;
  /** Null while a read is in flight, including the refresh a write triggers. */
  readonly scopeState: ScopeStateView | null;
  /**
   * Why the catalog read failed, or null.
   *
   * Reported HERE rather than by the page for the same reason the placeholder is:
   * a parent that swapped this tab for an error message would unmount the
   * mutation hook and erase the outcome of a write that had already landed.
   */
  readonly offeringsError: string | null;
  /** Why the scope-state read failed, or null. Same reason it is reported here. */
  readonly scopeStateError: string | null;
  readonly capabilities: readonly Capability[];
  /**
   * The platform-staff permissions this operator holds, for deciding whether an
   * action reads as available. The server is still the authority; this only
   * decides what the control looks like before it is pressed.
   */
  readonly permissions: readonly string[];
  /** Fired after a write lands, so the page re-reads what now applies. */
  readonly onMutated: () => void;
}

/**
 * The offering catalog at one scope, grouped by the service that owns the
 * capabilities underneath.
 *
 * Grouping by OWNING SERVICE rather than by product area is deliberate for this
 * surface: capabilities self-register per service, so a service that ships a new
 * one appears here as its own section with no change to this file. Product area
 * is the customer page's grouping and is shown per card, not as the spine.
 *
 * ## Why every read state lives HERE
 *
 * This tab owns the write, so it also owns every state a read can be in: the
 * placeholder, the catalog error, and the scope-state error. It has to. A write
 * triggers a re-read, and a parent that swapped this tab for any of those three
 * while the re-read ran would unmount the mutation hook and discard the outcome
 * before the operator could read it. A failed write fires no re-read, so a
 * SUCCESSFUL write was the one outcome that could be lost this way.
 *
 * The error case is the worst of the three, not a lesser one: the write
 * COMMITTED, the re-read then failed, so the banner is the only record of it.
 * Replacing the panel with "Failed to read this scope" would leave the operator
 * with nothing at all about the privileged write they just performed.
 *
 * That is why the banner is rendered ABOVE the branch rather than inside it.
 * Every read state is something that appears below a result that persists.
 *
 * One write at a time across the whole tab, and one place that reports how it
 * went. A per-card status line would let two cards make different claims about
 * a scope they both describe.
 */
export function OfferingsTab({
  offerings,
  scope,
  scopeState,
  offeringsError,
  scopeStateError,
  capabilities,
  permissions,
  onMutated,
}: OfferingsTabProps): React.ReactElement {
  /* Deliberately survives a scope change, unlike the confirmation inside
     `OfferingGrid`, which the scope key destroys. The difference is what each
     one asserts: a confirmation is a claim about one scope's rows, while this is
     a view over a catalog that is the same at every scope. A key naming no
     current section already falls back to showing everything, so a surviving
     filter cannot hide the catalog either. Cosmetic, and the honest default is
     to leave it alone. */
  const [selected, setSelected] = useState<string | null>(null);

  const writeBlock = offeringWriteBlock(scope, permissions);
  const mutation = useOfferingMutation({ scope, onMutated });
  const busy = mutation.pending !== null;

  /* A view for any other scope is not an answer about this one. The page's own
     read guard makes this unreachable today; checking it here means a future
     change that breaks that cannot quietly paint one customer's entitlements
     under another customer's name. */
  const view =
    scopeState !== null && scopeKey(scopeState.scope) === scopeKey(scope) ? scopeState : null;

  const sections =
    offerings === null || view === null
      ? null
      : buildOfferingSections({ offerings, scopeState: view, capabilities });

  /* Filtering by the section KEY, not by the label: two services cannot share
     a key, while a service literally named "No owning service" would share the
     fallback section's caption.

     A key that names no current section falls back to showing everything. The
     chip row is rebuilt from `sections`, so a stale selection would otherwise
     hide every offering behind a chip that is no longer on screen to unpress. */
  const matching: readonly OfferingSection[] =
    sections === null || selected === null
      ? (sections ?? [])
      : sections.filter((section) => sectionKey(section.namespace) === selected);
  const shown = sections !== null && matching.length === 0 ? sections : matching;

  return (
    <div className="space-y-6" data-testid="offerings-tab">
      {/* Above every early state, so an outcome outlives the read a write
          triggers. Moving these inside the branches below would put the result
          of a privileged write behind a loading placeholder. */}
      {mutation.error !== null && (
        <p className="text-sm text-destructive" data-testid="offering-mutation-error">
          {mutation.error}
        </p>
      )}
      {mutation.result !== null && (
        <p className="text-sm text-muted-foreground" data-testid="offering-mutation-result">
          {mutation.result}
        </p>
      )}

      {offeringsError !== null ? (
        <p className="text-sm text-destructive" data-testid="offerings-error">
          Failed to load the offering catalog: {offeringsError}
        </p>
      ) : scopeStateError !== null ? (
        <p className="text-sm text-destructive" data-testid="scope-state-error">
          Failed to read this scope: {scopeStateError}
        </p>
      ) : sections === null ? (
        <p className="text-sm text-muted-foreground" data-testid="offerings-loading">
          Reading what applies to this scope…
        </p>
      ) : sections.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="offerings-empty">
          The catalog publishes no offerings.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="service-filter-chips">
            <Button
              type="button"
              size="sm"
              variant={selected === null ? "default" : "outline"}
              aria-pressed={selected === null}
              data-testid="service-chip-all"
              onClick={() => setSelected(null)}
            >
              All {sections.reduce((total, section) => total + section.offerings.length, 0)}
            </Button>
            {sections.map((section) => {
              const key = sectionKey(section.namespace);
              return (
                <Button
                  key={key}
                  type="button"
                  size="sm"
                  variant={selected === key ? "default" : "outline"}
                  aria-pressed={selected === key}
                  data-testid={`service-chip-${key}`}
                  onClick={() => setSelected(selected === key ? null : key)}
                >
                  {sectionLabel(section.namespace)} {section.offerings.length}
                </Button>
              );
            })}
          </div>

          {/* Keyed on the scope, which is what makes a scope change DESTROY the
              confirmation inside rather than park it. Hiding a stale one stops
              the wrong-target press but leaves it to reappear on the way back;
              destroying it cannot. */}
          <OfferingGrid
            key={scopeKey(scope)}
            sections={shown}
            allSections={sections}
            scope={scope}
            writeBlock={writeBlock}
            busy={busy}
            onRun={mutation.run}
          />
        </>
      )}
    </div>
  );
}
